import React, { useState } from "react";
import { Megaphone, Send, Users, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const AdminAnnouncementPanel: React.FC = () => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendPush, setSendPush] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const handleSendAnnouncement = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error("Please provide both title and message");
      return;
    }

    setIsSending(true);
    try {
      // Get all user profiles to notify
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id");

      if (profilesError) throw profilesError;

      if (!profiles || profiles.length === 0) {
        toast.error("No users to notify");
        return;
      }

      // Create notifications for all users in batches
      const notifications = profiles.map((profile) => ({
        user_id: profile.id,
        type: "announcement",
        title: `📢 ${title}`,
        body: message,
        data: { announcement: true },
      }));

      // Insert in batches of 100
      const batchSize = 100;
      for (let i = 0; i < notifications.length; i += batchSize) {
        const batch = notifications.slice(i, i + batchSize);
        const { error } = await supabase.from("notifications").insert(batch);
        if (error) throw error;
      }

      toast.success(`Announcement sent to ${profiles.length} users!`);
      setTitle("");
      setMessage("");
    } catch (error) {
      console.error("Error sending announcement:", error);
      toast.error("Failed to send announcement");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-primary" />
        <h3 className="font-bold text-foreground">Broadcast Announcement</h3>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">Title *</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Love Exhibition is Live!"
          maxLength={100}
        />
      </div>

      {/* Message */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">Message *</label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Write your announcement message..."
          rows={4}
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground text-right">{message.length}/500</p>
      </div>

      {/* Options */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-medium">Send Push Notification</span>
        </div>
        <Switch checked={sendPush} onCheckedChange={setSendPush} />
      </div>

      {/* Info */}
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
        <Users className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm">This will notify all users on the platform</span>
      </div>

      {/* Send Button */}
      <Button
        onClick={handleSendAnnouncement}
        disabled={isSending || !title.trim() || !message.trim()}
        className="w-full gap-2"
      >
        {isSending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            Broadcast Announcement
          </>
        )}
      </Button>
    </div>
  );
};

export default AdminAnnouncementPanel;
